import { z } from "zod";

import { baseTimeDataPointSchema } from "./carton-production";

export const conversionMetricItemSchema = z.object({
	count: z.number(),
	percentage: z.number(),
});
export type ConversionMetricItem = z.infer<typeof conversionMetricItemSchema>;

export const successfulClosingSegmentSchema = z.object({
	totalCount: z.number(),
	totalPercentage: z.number(),
	closedOnFirstAttempt: conversionMetricItemSchema,
	closedOnReattempt: conversionMetricItemSchema,
});
export type SuccessfulClosingSegment = z.infer<typeof successfulClosingSegmentSchema>;

export const resolvedClosingSegmentSchema = z.object({
	totalCount: z.number(),
	totalPercentage: z.number(),
	closedManually: conversionMetricItemSchema,
	closingRemoved: conversionMetricItemSchema,
});
export type ResolvedClosingSegment = z.infer<typeof resolvedClosingSegmentSchema>;

export const abandonedClosingSegmentSchema = z.object({
	totalCount: z.number(),
	totalPercentage: z.number(),
	closingFailed: conversionMetricItemSchema,
	closingExpired: conversionMetricItemSchema,
});
export type AbandonedClosingSegment = z.infer<typeof abandonedClosingSegmentSchema>;

export const cartonClosingConversionRateSchema = z.object({
	totalJobs: z.number(),
	successfulConversionRatePercentage: z.number(),
	successful: successfulClosingSegmentSchema,
	resolved: resolvedClosingSegmentSchema,
	abandoned: abandonedClosingSegmentSchema,
});
export type CartonClosingConversionRateResponse = z.infer<typeof cartonClosingConversionRateSchema>;

const cartonClosingTimeDataSchema = baseTimeDataPointSchema.extend({
	cartonsClosed: z.number(),
	cartonsClosingFailed: z.number(),
	isoWeek: z.number().optional(),
	isoYear: z.number().optional(),
});

const cartonClosingByMachineSchema = z.object({
	machineName: z.string(),
	cartonsClosed: z.number(),
	cartonsClosingFailed: z.number(),
	percentage: z.number().optional(),
});

export const cartonClosingApiResponseSchema = z.object({
	totalCartonsClosed: z.number(),
	totalCartonsClosingFailed: z.number(),
	averageTimeToClose: z.number().optional(),
	averageClosingCycleTime: z.number().optional(),
	conversionRate: cartonClosingConversionRateSchema.optional(),
	byMachine: z.array(cartonClosingByMachineSchema),
	overTime: z.array(
		z.object({
			machineName: z.string(),
			machineId: z.string(),
			data: z.array(cartonClosingTimeDataSchema),
		}),
	),
	isScheduleApplied: z.boolean().optional(),
});

export type CartonClosingApiResponse = z.infer<typeof cartonClosingApiResponseSchema>;

export const DEFAULT_CARTON_CLOSING_CONVERSION_RATE: CartonClosingConversionRateResponse = {
	totalJobs: 0,
	successfulConversionRatePercentage: 0,
	successful: {
		totalCount: 0,
		totalPercentage: 0,
		closedOnFirstAttempt: { count: 0, percentage: 0 },
		closedOnReattempt: { count: 0, percentage: 0 },
	},
	resolved: {
		totalCount: 0,
		totalPercentage: 0,
		closedManually: { count: 0, percentage: 0 },
		closingRemoved: { count: 0, percentage: 0 },
	},
	abandoned: {
		totalCount: 0,
		totalPercentage: 0,
		closingFailed: { count: 0, percentage: 0 },
		closingExpired: { count: 0, percentage: 0 },
	},
};

export const MOCK_CARTON_CLOSING_CONVERSION_RATE: CartonClosingConversionRateResponse = {
	totalJobs: 1000,
	successfulConversionRatePercentage: 61.0,
	successful: {
		totalCount: 610,
		totalPercentage: 61.0,
		closedOnFirstAttempt: {
			count: 528,
			percentage: 52.8,
		},
		closedOnReattempt: {
			count: 82,
			percentage: 8.2,
		},
	},
	resolved: {
		totalCount: 230,
		totalPercentage: 23.0,
		closedManually: {
			count: 12,
			percentage: 1.2,
		},
		closingRemoved: {
			count: 218,
			percentage: 21.8,
		},
	},
	abandoned: {
		totalCount: 160,
		totalPercentage: 16.0,
		closingFailed: {
			count: 128,
			percentage: 12.8,
		},
		closingExpired: {
			count: 32,
			percentage: 3.2,
		},
	},
};

export const DEFAULT_CARTON_CLOSING_API_RESPONSE: CartonClosingApiResponse = {
	totalCartonsClosed: 0,
	totalCartonsClosingFailed: 0,
	averageTimeToClose: 0,
	averageClosingCycleTime: 0,
	conversionRate: DEFAULT_CARTON_CLOSING_CONVERSION_RATE,
	byMachine: [],
	overTime: [],
};

export const MOCK_CARTON_CLOSING_API_RESPONSE: CartonClosingApiResponse = {
	totalCartonsClosed: 610,
	totalCartonsClosingFailed: 128,
	averageTimeToClose: 4.5,
	averageClosingCycleTime: 12.2,
	conversionRate: MOCK_CARTON_CLOSING_CONVERSION_RATE,
	byMachine: [],
	overTime: [],
};

export type CartonClosingNavigation = "" | "closedByMachine";
